import { useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Container,
  Field,
  Flex,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useColorModeValue } from "../components/chakra/color-mode.tsx";
import { AiFillAlert as AlertIcon, AiFillLock } from "react-icons/ai";
import Logo from "../../Layout/Logo.tsx";
import { isDesktop } from "react-device-detect";
// import { useAuth0Passwordless } from "./Auth0Provider.tsx";

// Utility to detect email vs phone
const isEmail = (input) => /\S+@\S+\.\S+/.test(input);
const isPhone = (input) => /^\+?[0-9]{7,15}$/.test(input);

const PasswordlessLogin = ({ domain, clientId, onLogin }) => {
  // const { loginWithTokens } = useAuth0Passwordless();
  const [input, setInput] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const bg = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("gray.50", "gray.700");

  const sendCode = async () => {
    try {
      setError("");
      let correctedInput = input;

      let connection;
      if (isEmail(input)) {
        connection = "email";
      } else if (isPhone(input)) {
        connection = "sms";
        correctedInput = input.replace(/^(?!\+1)/, "+1");
      } else {
        setError(
          "Enter a valid email or phone number (E.164 format for phone).",
        );
        return;
      }

      await axios.post(`https://${domain}/passwordless/start`, {
        client_id: clientId,
        connection,
        send: "code",
        [connection === "email" ? "email" : "phone_number"]: correctedInput,
      });

      setCodeSent(true);
    } catch (err) {
      console.error(err);
      if (
        err?.response?.data?.error_description == "Public signup is disabled"
      ) {
        setError("Your account isn't set up yet. Please contact an admin.");
      } else {
        setError("Failed to send code. Check your input or your Auth0 setup.");
      }
    }
  };

  const verifyCode = async () => {
    try {
      setError("");

      const connection = isEmail(input) ? "email" : "sms";
      let correctedInput = input;
      if (connection === "sms") {
        correctedInput = input.replace(/^(?!\+1)/, "+1");
      }

      const body = await axios.post(`https://${domain}/oauth/token`, {
        grant_type: "http://auth0.com/oauth/grant-type/passwordless/otp",
        client_id: clientId,
        username: correctedInput,
        otp,
        realm: connection,
        scope: "openid profile email",
      });

      const claims = jwtDecode(body.data.access_token);
      const id = jwtDecode(body.data.id_token);

      const {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        nickname,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        picture,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        updated_at,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        email,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        email_verified,
        sub,
        exp,
        aud,
      } = id;

      const item = {
        body: {
          ...body.data,
          decodedToken: {
            claims,
            user: {
              nickname,
              name: name,
              picture,
              updated_at,
              email: email,
              email_verified,
              sub,
            },
            aud,
          },
        },
        expiresAt: exp,
      };

      window.localStorage.setItem("auth0creds", JSON.stringify(item));

      // response.data contains access_token and id_token
      // onLogin(response.data);
      // await loginWithTokens(response.data);
      if (onLogin) onLogin();
    } catch (err) {
      console.error(err);
      setError("Invalid code. Please try again.");
    }
  };

  return (
    <Container
      minH="100vh"
      h={"100vh"}
      minW={"100vw"}
      display="flex"
      alignItems={isDesktop ? "center" : "flex-start"}
      py={isDesktop ? 0 : 4}
      justifyContent="center"
      bg={bg}
      px={4}
    >
      <Box
        w={isDesktop ? "md" : "100%"}
        h={isDesktop ? "md" : "100%"}
        style={{ flex: isDesktop ? 0.5 : 1 }}
        bgGradient={`linear(to-b, ${cardBg}, ${useColorModeValue("white", "gray.600")})`}
        p={8}
        borderRadius="2xl"
        boxShadow="lg"
        borderWidth="1px"
        borderColor={useColorModeValue("gray.200", "gray.600")}
      >
        <VStack
          gap={6}
          h={"100%"}
          align="stretch"
          justifyContent={"space-between"}
        >
          <Flex flexFlow={"column nowrap"} alignItems={"center"}>
            <Logo
              style={{
                maxWidth: "200px",
                marginBottom: "1rem",
                marginTop: "1rem",
              }}
            />
          </Flex>
          {!isDesktop && (
            <Flex flexFlow={"column nowrap"} alignItems={"center"}>
              <AiFillLock size={"9rem"} />
            </Flex>
          )}
          <div>
            <Flex flexFlow={"column nowrap"} alignItems={"center"} pb={6}>
              <Heading size="2xl">Login</Heading>
              <Text mt={2} color="muted" textAlign={"center"}>
                Sign in with an email address or phone number. No password
                required.
              </Text>
            </Flex>

            {!codeSent ? (
              <div>
                <Field.Root id="identifier" invalid={!!error}>
                  <Field.Label>Email or Phone</Field.Label>
                  <Input
                    placeholder="example@mail.com or +1234567890"
                    size="lg"
                    variant={"subtle"}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        sendCode();
                      }
                    }}
                  />
                  {error && (
                    <Alert.Root status="error" borderRadius="md">
                      <Alert.Indicator>
                        <AlertIcon />
                      </Alert.Indicator>
                      <Alert.Content>{error}</Alert.Content>
                    </Alert.Root>
                  )}
                </Field.Root>

                <HStack gap={3} pt={4}>
                  <Button
                    w="full"
                    size="lg"
                    colorScheme="teal"
                    onClick={sendCode}
                  >
                    Send Code
                  </Button>
                </HStack>
              </div>
            ) : (
              <>
                <Field.Root id="otp" invalid={!!error}>
                  <Field.Label>Verification code</Field.Label>
                  <Input
                    placeholder="Enter the 6-digit code"
                    size="lg"
                    variant={"subtle"}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        verifyCode();
                      }
                    }}
                  />
                  {error && (
                    <Alert.Root status="error" borderRadius="md">
                      <Alert.Indicator>
                        <AlertIcon />
                      </Alert.Indicator>
                      <Alert.Content>{error}</Alert.Content>
                    </Alert.Root>
                  )}
                </Field.Root>

                <HStack gap={3} pt={4}>
                  <Button
                    size="lg"
                    flex={1}
                    colorScheme="teal"
                    onClick={verifyCode}
                  >
                    Verify Code
                  </Button>
                  <Button
                    size={"lg"}
                    variant={"subtle"}
                    onClick={() => {
                      setCodeSent(false);
                      setOtp("");
                      setError("");
                    }}
                  >
                    Back
                  </Button>
                </HStack>
              </>
            )}
          </div>

          {/** Optional inline alert for general errors/successes */}
          {/* Use Chakra's Alert for clearer UX */}
        </VStack>
      </Box>
    </Container>
  );
};

export default PasswordlessLogin;
